import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, Target, ChevronDown, ChevronUp, BookOpen, CheckCircle, Shield, Scale, CloudRain, TrendingUp } from 'lucide-react';

export default function SnapshotBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/snapshot')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-snapshot"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Snapshot Analysis
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <Target className="h-5 w-5 sm:h-7 sm:w-7" />
              Benefits of Snapshot Analysis
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Snapshot Analysis</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Snapshot Analysis provides a fast, data-driven assessment of whether the biomass inside a cage matches expectations. By comparing the observed biological FCR against a validated model at a specific average weight and date, it highlights inconsistencies that are often invisible in day-to-day operations.
                  </p>
                </section>

                {/* Benefit 1: Early Detection */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-full flex-shrink-0">
                      <Shield className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-2">Early Detection of Missing Fish</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The key benefit is early detection of missing or unaccounted fish. Deviations in FCR can signal losses caused by predation, handling events, storm damage, cannibalism, incorrect loading numbers, or mortality reporting errors—allowing farms to investigate and respond before problems compound.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 2: Decision Accuracy */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                      <Target className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-2">Improved Decision Accuracy</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Snapshot Analysis also improves decision accuracy. By anchoring the analysis to a precise average weight and date, it reduces uncertainty caused by outdated samples or assumptions. This leads to more reliable biomass estimates, better feed planning, and improved production forecasts.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 3: Validation Checkpoint */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-2">Data Validation Checkpoint</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Operationally, Snapshot Analysis acts as a validation checkpoint for farm data. It helps confirm whether current performance reflects true biological efficiency or whether discrepancies are driven by fish numbers rather than feeding strategy or health.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Benefit 4: Feeding Accuracy */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full flex-shrink-0">
                      <Scale className="h-4 w-4 text-[#0891b2]" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-[#0891b2] mb-2">Critical Impact on Feeding Accuracy</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        An additional critical benefit of Snapshot Analysis is its impact on feeding accuracy. Feeding plans are built on expected biomass; when fish numbers inside the cage are incorrect, feeding decisions become misaligned.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="font-medium text-red-700 mb-1 text-xs">If Fewer Fish Than Assumed:</p>
                          <p className="text-xs text-gray-700">
                            The cage is effectively overfed, resulting in elevated feed costs, increased biological FCR, and unnecessary waste.
                          </p>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <p className="font-medium text-orange-700 mb-1 text-xs">If More Fish Than Expected:</p>
                          <p className="text-xs text-gray-700">
                            The cage is underfed, leading to suppressed growth performance, longer production cycles, and reduced efficiency.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Conclusion */}
                <section className="bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-200 rounded-full flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-slate-800 mb-2">Protecting Margins & Performance</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        By identifying mismatches between expected and actual fish numbers, Snapshot Analysis helps ensure that feed is allocated to the fish that are actually present, protecting margins while maintaining optimal growth performance.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Storm Loss Detection */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                      <CloudRain className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-2">Storm Loss Detection</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Snapshot Analysis is particularly valuable after storm events, which are known to cause significant but often poorly quantified losses. By comparing observed biological FCR against model expectations, Snapshot Analysis provides an effective method to identify missing fish following storms, even when losses are not immediately visible.
                      </p>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Snapshot Analysis"
              youtubeUrl={HELP_URLS.snapshotAnalysis.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/snapshot')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="start-analysis-btn"
              >
                <Target className="h-4 w-4 mr-2" />
                Start Snapshot Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
